/**
 * Centralized feature flag name constants.
 *
 * This module provides a single source of truth for feature flag names
 * used throughout the frontend. Using constants prevents typos and
 * makes it easy to find all usages of a particular flag.
 */

export const FeatureFlags = {
  INPUT_GUARDRAILS: 'input_guardrails',
  AGENT_CREATION_ENABLED: 'agent_creation_enabled',
  ALERTING_ENABLED: 'alerting_enabled',
  APP_COMPANION_ENABLED: 'app_companion_enabled',
  COLLABORATION_ENABLED: 'collaboration_enabled',
  CREDIT_LIMITING_ENABLED: 'credit_limiting_enabled',
  EXTERNAL_INTEGRATIONS_ENABLED: 'external_integrations_enabled',
  HEADLESS_ENABLED: 'headless_enabled',
  OCP_TOOLS_ENABLED: 'ocp_tools_enabled',
  PRIVATE_AGENTS_ENABLED: 'private_agents_enabled',
  RAG_ENABLED: 'RAG_enabled',
  WORKFLOWS_CHAT_ENABLED: 'workflows_chat_enabled',
  WORKFLOWS_FULL_ENABLED: 'workflows_full_enabled',

  // Existing flags (for union checks with new flags)
  PUBLISHER: 'publisher',
  AGENT_RESUME_ENABLED: 'agent_resume_enabled',
  CONFIGURED_PROMPT: 'configured_prompt',
  APP_COMPANION: 'app_companion',
  CONNECTORS: 'connectors',
  DIRECTORY: 'directory',
  DIRECTORY_TOOL_VALIDATION: 'directory_tool_validation',
  MANAGE_SYSTEM_TOOLS: 'manage_system_tools',
  // Per-user tool disable — lets an end user hide a specific tool from their
  // own tool lists while it stays enabled at the instance level.
  PER_USER_TOOL_DISABLE: 'per_user_tool_disable',
  // Registry (tool-group) capability summaries — gates the "Edit summary" dialog
  // on a registry row, and the matching "## Tool Registries" system-prompt section.
  TOOL_REGISTRY_SUMMARIES: 'tool_registry_summaries',
  // Force-shows desktop-scoped tool registries (client_type === 'desktop') on
  // the Registries tab even on web. Desktop-scoped tools are always shown inside
  // the Opal desktop app regardless; this flag only affects the web client.
  // Also gates the "download the desktop app" surfaces on web — the dismissible
  // banner below the global header and the Settings › Appearance card (both
  // self-hide inside the desktop shell).
  DESKTOP_TOOLS_ENABLED: 'desktop_tools_enabled',
  PLUGINS: 'plugins',
  REUSE_CANVAS: 'reuse_canvas',
  VOICE_DICTATION: 'voice_dictation',
  WORKFLOW_UX: 'workflow_ux',
  // Gates the "Organic" authoring toggle + organic execution-viewing surface.
  ORGANIC_WORKFLOW_AGENT: 'organic_workflow_agent',
  // Gates the "Private Executions" toggle on specialized/workflow agent forms.
  // Pure frontend release gate — backend accepts private_executions=true
  // from any authorized editor regardless of this flag's state (see
  // docs/tech-spec/hypatia/private-executions.md §6). Once GA, delete this
  // entry and its consumers.
  PRIVATE_EXECUTIONS: 'private-executions',
  // ACL / Permissions
  PERMISSION: 'permission',

  // SDK / Developer
  AGENT_SDK: 'agent_sdk',

  // VAU
  // Boolean `slack_im_ability` variable gates the Slack Ability Tools registry
  // row on the Registries/Tools tabs (and its individual tools) — hidden from
  // the table entirely while off, mirroring DESKTOP_TOOLS_ENABLED's row-filter
  // pattern below.
  //
  // Boolean `email_ability` variable gates the teammate email surface: the
  // Inbox nav item + its routes on the VAU detail page (and with them the
  // inbox settings modal, whose only entry point is the cog on that page),
  // plus the irreversible "Provision VAU email" action on Settings → Email
  // Domain. Hidden while off rather than disabled. Purely a frontend release
  // gate — hypatia force-enables the matching `vau-email-inbox` instance
  // capability, so the backend accepts these calls regardless. See
  // docs/tech-spec/vau/email-ability.md §9.1 / §10.1.
  VAU: 'vau',

  // Feature flag to enable the CODE step type in workflow builder (CE sandbox execution)
  CODE_STEP_ENABLED: 'code_step_enabled',

  // Brands. The Optimizely integration key predates the product rename.
  BRANDS: 'brand_packs',

  // Artifacts — gates the "Stack elements" toggle that folds the Artifacts
  // tab into per-Space stacks (stacked list + deck grid views).
  ARTIFACTS_GROUPING: 'artifacts-grouping',

  // MISC
  CORS_DOMAIN: 'cors_domain',
  MESSAGING_ENABLED: 'messaging_enabled',
  NOTIFICATIONS: 'notifications',
  SKILL_UX: 'skill_ux',

  // Gates the workspace-tools defaults + tier-1/tier-2 warnings on the
  // specialized-agent create form. Mirrors the backend feature flag that
  // drives TMS-side large-tool-response offload and the primary-agent /
  // hypatia x-opal-can-handle-offload header. See
  // docs/tech-spec/tools/large-tool-response-caller-handshake.md.
  LARGE_TOOL_RESPONSE: 'large-tool-response',

  // Multi-provider gate. Its boolean `openai` variable gates OpenAI-on-Azure
  // (GPT-5.6) as a selectable model provider — the option stays hidden until
  // `openai` is true, so the backend/provider plumbing can ship dark. Gates
  // *selectability* only: an instance already configured to `openai` still
  // renders its label with the variable off. The backend mirrors this —
  // primary-agent / hypatia fall back to google when the variable is off (an
  // instance can't be stranded on a provider the flag hasn't enabled). The
  // flag's other variables (provider / kimi / deepseek) are reserved for future
  // providers and untouched here.
  // See docs/tech-spec/openai-azure/decisions.md (Decision 5B).
  LLM_PROVIDER: 'llm_provider',
} as const;

export type FeatureFlagName = (typeof FeatureFlags)[keyof typeof FeatureFlags];
